import { PurchaseOrderItem } from "@/types/purchase";
import { AddPackBoxItem, BoxItem, PackSourceItem, PackSourceItemLot } from "@/types/packing";
import { copyDoc } from "@/utils/file/upload";

/**
 * 按 productId+lot 从 purLotMap 匹配采购明细，回填每条分配明细的申报单价/币种。
 * 未匹配到的明细保持原值不动。就地修改（items 为分配新产生的对象）。
 */
export function applyLotPrice(
  items: AddPackBoxItem[],
  purLotMap: Record<string, PurchaseOrderItem>,
  getCurrencyDesc: (key?: string) => string | undefined,
): void {
  (items || []).forEach(it => {
    const purItem = purLotMap[`${it.productId}<->${it.lot}`];
    if (purItem) {
      it.declaredUnitPrice = Number(purItem.priceStr ?? "0");
      const desc = getCurrencyDesc(purItem.order?.currency);
      if (desc !== undefined) it.declaredCurrency = desc;
    }
  });
}

/**
 * 分配当前箱子的装箱明细：
 * 优先从选中的批次（selLot）取数，不足时按顺序从其他剩余批次凑数，
 * 直至装满 form.count 或批次剩余量耗尽。
 *
 * 例：商品剩余 300，批次 X/Y/Z 剩余 10/200/90，选 Y，form.count=100
 *   → [Y:100]
 *
 * @param sourceItems - 可装箱的商品源数据（会被克隆后扣减剩余量）
 * @param productId   - 选中的商品 ID
 * @param selLot      - 选中的批次（优先取数）
 * @param form        - 基础表单数据（lot / count 按拆分结果覆盖，其余字段原样继承）
 * @returns 当前箱明细数组 + 扣减后的新 sourceItems
 */
export function allocateCurrentBoxItems(
  sourceItems: PackSourceItem[],
  productId: string,
  selLot: PackSourceItemLot,
  form: AddPackBoxItem,
): { items: AddPackBoxItem[]; newSourceItems: PackSourceItem[] } {
  const idx = sourceItems.findIndex(s => s.productId === productId);
  if (idx < 0 || !form.count || form.count <= 0) {
    return { items: [], newSourceItems: sourceItems };
  }
  const product = sourceItems[idx];
  // 克隆批次，选中批次置顶，其余按原顺序排后；只取有剩余量的
  const newLots = (product.lots ?? []).map(l => ({ ...l }));
  const selIdx = newLots.findIndex(l => l.lot === selLot.lot);
  const pool = (selIdx > -1
    ? [newLots[selIdx], ...newLots.filter((_, i) => i !== selIdx)]
    : newLots
  ).filter(l => (l.remainingCount || 0) > 0);

  const items: AddPackBoxItem[] = [];
  let need = form.count;
  let weightFlag: boolean = true;
  let poolIdx = 0;
  while (need > 0 && poolIdx < pool.length) {
    const cur = pool[poolIdx];
    const take = Math.min(need, cur.remainingCount);
    cur.remainingCount -= take;
    need -= take;
    items.push({ ...form, lot: cur.lot, count: take, netWeight: weightFlag ? form.netWeight : 0 });
    weightFlag = false;
    if (cur.remainingCount <= 0) poolIdx++;
  }

  const taken = form.count - need;
  const newSourceItems = sourceItems.map((s, i) => i === idx
    ? { ...s, lots: newLots, remainingCount: (s.remainingCount || 0) - taken }
    : s);
  return { items, newSourceItems };
}

export interface CopyGroupTarget {
  key: string;
  productId: string;
  brand: string;
  count: number;      // 当前箱该组合计数量
  netWeight: number;  // 当前箱该组合计净重
  template: AddPackBoxItem; // 组内第一条明细作为新明细模板
}

/**
 * 按productId+brand聚合箱子明细，整合count、netWeight，作为新箱复制的分配目标
 */
export function aggregateBoxItems(
  boxItems: readonly BoxItem[],
): CopyGroupTarget[] {
  const map = new Map<string, CopyGroupTarget>();
  (boxItems || []).forEach(it => {
    const key = `${it.productId}__${it.brand ?? ''}`;
    const cur = map.get(key);
    if (cur) {
      cur.count += it.count || 0;
      cur.netWeight += it.netWeight || 0;
    } else {
      map.set(key, {
        key,
        productId: it.productId,
        brand: it.brand ?? '',
        count: it.count || 0,
        netWeight: it.netWeight || 0,
        template: {
          productId: it.productId,
          lot: it.lot,
          count: it.count,
          declaredUnitPrice: it.declaredUnitPrice,
          declaredCurrency: it.declaredCurrency,
          hsCode: it.hsCode,
          originPlace: it.originPlace,
          productNameCn: it.productNameCn,
          productNameEn: it.productNameEn,
          remark: it.remark,
          brand: it.brand,
          brandType: it.brandType,
          exportBrandName: it.exportBrandName,
          netWeight: it.netWeight,
          docId: it.docId || ''
        },
      });
    }
  });
  return Array.from(map.values());
}

/**
 * 为新箱分配明细：每个新箱以当前箱的（productId+brand）聚合组成为目标，
 * 按 productId 从 sourceItems 的剩余批次中依次取数（批次可跨箱耗尽），能分配多少分配多少。
 * sourceItems 本身不区分 brand，同 productId 的多个 brand 组会依次消耗同一批批次。
 *
 * @param sourceItems - 当前箱装完后已扣减的 sourceItems（克隆后继续扣减）
 * @param groups      - 当前箱聚合出的分配目标
 * @param boxCount    - 新箱数量
 * @returns 每个新箱的明细数组 + 扣减后的新 sourceItems
 */
export async function allocateCopyBoxItems(
  sourceItems: PackSourceItem[],
  groups: CopyGroupTarget[],
  boxCount: number,
): Promise<{ groupsPerBox: AddPackBoxItem[][]; newSourceItems: PackSourceItem[] }> {
  const newSourceItems = sourceItems.map(s => ({ ...s, lots: (s.lots ?? []).map(l => ({ ...l })) }));
  const groupsPerBox: AddPackBoxItem[][] = [];
  if (!groups.length || boxCount <= 0) return { groupsPerBox, newSourceItems };

  for (let box = 0; box < boxCount; box++) {
    const items: AddPackBoxItem[] = [];
    for (const g of groups) {
      const product = newSourceItems.find(s => s.productId === g.productId);
      const pool = (product?.lots ?? [])
        .filter(l => (l.remainingCount || 0) > 0);
      let need = g.count;
      let weightFlag: boolean = true;
      let docFlag: boolean = true;  //复制过后置为false
      let poolIdx = 0;
      while (need > 0 && poolIdx < pool.length) {
        const cur = pool[poolIdx];
        const take = Math.min(need, cur.remainingCount);
        cur.remainingCount -= take;
        need -= take;
        //复制doc逻辑
        let newDocId: string | undefined = undefined;
        if (!!g.template.docId && docFlag) {
          let newDoc = await copyDoc(g.template.docId);
          if (newDoc) newDocId = newDoc.id;
          docFlag = false
        }
        items.push({ ...g.template, docId: newDocId, lot: cur.lot, count: take, netWeight: weightFlag ? g.netWeight : 0 });
        weightFlag = false;
        if (cur.remainingCount <= 0) poolIdx++;
      }
    }
    groupsPerBox.push(items);
  }
  return { groupsPerBox, newSourceItems };
}

/**
 * 生成以指定字符串开头、数字部分连续递增的字符串数组
 * 支持格式：
 *   - 字母 + 数字（如 'A001', 'AB123'）
 *   - 纯数字（如 '001', '9999', '23456'）
 * @param start - 起始字符串
 * @param count - 生成数量（从 start 的下一个开始）
 * @returns 生成的字符串数组
 * @throws {Error} 当格式不合法时抛出
 */
export function generateSequence(start: string, count: number): string[] {
  if (count <= 0) {
    return [];
  }

  let prefix: string = '';        // 字母前缀（可能为空）
  let numStr: string;             // 数字字符串

  // 尝试匹配：字母开头 + 数字结尾
  const match1 = start.match(/^([A-Za-z]+)(\d+)$/);
  if (match1) {
    prefix = match1[1];
    numStr = match1[2];
  } else {
    // 尝试匹配：纯数字
    const match2 = start.match(/^(\d+)$/);
    if (match2) {
      numStr = match2[1];
      // prefix 保持为空
    } else {
      throw new Error('字符串格式错误：须为字母开头+数字 或 纯数字');
    }
  }

  const baseNum = parseInt(numStr, 10);
  const originalLen = numStr.length;

  return Array.from({ length: count }, (_, index) => {
    const nextValue = baseNum + index + 1;
    let nextStr = String(nextValue);

    // 补零：仅当新数字位数小于原位数时补足
    if (nextStr.length < originalLen) {
      nextStr = nextStr.padStart(originalLen, '0');
    }

    return prefix + nextStr;
  });
};
